import React, { useEffect, useState, ReactText, useContext, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import {
  DualListBox,
  SearchableDropdown,
  SearchableDropdownOption,
  OptionPropType,
  Button,
  Notifier,
  notify,
  PageLoader,
} from '@scholastic/volume-react';
import { CSVLink } from 'react-csv';
import { fetchInstitutions } from '../../state/StatsReport.api';
import {
  GroupOptions,
  InstitutionListOptions,
  transformGroups,
  transformInstitutionList,
} from './StatsDataTransform';
import { fetchChartData } from '../../state/ReportsChart.api';
import {
  TransformChartDataResponse,
  transformIndividualChartData,
} from '../ReportCharts/ReportDataTransform';
import './StatsReport.scss';

import { StoreContext, ActionType } from '../../state/store';
import { transformTableData } from '../ReportCharts/ReportChartBody/ReportTable/ReportTable.transformer';

const StatsReport = () => {
  const { state, dispatch } = useContext(StoreContext);
  const [selectedInstitution, setSelectedInstitution] = useState<OptionPropType[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<SearchableDropdownOption>();
  const [availableOptions, setAvailableOptions] = useState<InstitutionListOptions[]>([]);
  const [availabledGroup, setAvailableGroup] = useState<GroupOptions[]>([]);
  const [masterSchoolList, setMasterSchoolList] = useState<InstitutionListOptions[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dataForDownload, setDataForDownload] = useState<any>([]);
  const [itemsAreLoaded, setItemsAreLoaded] = useState<boolean>(false);

  const individualSiteRequestObj = state.individualSiteInfo;

  let prevUser = useRef({ selectedInstitution });

  useEffect(() => {
    try {
      loadAvailableOptions();
    } catch (err) {
      notify.warning('Something went wrong, please try again later');
      console.error(err);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      prevUser.current.selectedInstitution.length !== 0 &&
      prevUser.current.selectedInstitution.length === selectedInstitution.length
    ) {
      notify.warning('Please select at least one Site to move');
    } else if (
      prevUser.current.selectedInstitution.length === 0 &&
      prevUser.current.selectedInstitution.length === selectedInstitution.length &&
      availableOptions.length !== 0
    ) {
      notify.warning('Please select at least one Site to move');
    } else {
      prevUser.current = { selectedInstitution };
    }
    // eslint-disable-next-line
  }, [selectedInstitution]);

  useEffect(() => {
    if (selectedGroup) {
      const loadAvailableOptionsByGroup = (groupId: string | undefined) => {
        let institutionListByGroup: InstitutionListOptions[];
        if (groupId) {
          institutionListByGroup = masterSchoolList.filter((option) => option.group === groupId);
        } else {
          institutionListByGroup = masterSchoolList;
        }

        if (selectedInstitution.length > 0) {
          institutionListByGroup = institutionListByGroup.filter(
            (institution: { value: ReactText }) =>
              !selectedInstitution.find((selected) => selected.value === institution.value),
          );
        }
        setAvailableOptions(institutionListByGroup);
      };

      loadAvailableOptionsByGroup(selectedGroup.value);
    }
  }, [selectedGroup, masterSchoolList, selectedInstitution]);

  const loadAvailableOptions = async () => {
    if (individualSiteRequestObj) {
      // call api to fetch institution list
      const requestObj = {
        request: individualSiteRequestObj?.reportType,
        cuid: individualSiteRequestObj?.cuId,
      };
      const institutionList = await fetchInstitutions(requestObj);
      const availableShoolList = transformInstitutionList(institutionList.result.individualsites);

      setAvailableOptions(availableShoolList);
      setMasterSchoolList(availableShoolList);

      const availableGroupList = transformGroups(institutionList.result.groupcodes);
      setAvailableGroup(availableGroupList);

      let element: HTMLElement = document.querySelector(
        '[aria-label="Move all right"]',
      ) as HTMLElement;
      element.click();
    }
  };

  const createReport = async () => {
    setIsLoading(true);
    const requestBody = {
      institutions: selectedInstitution,
      ...individualSiteRequestObj.requestBody,
    };
    if (individualSiteRequestObj.requestBody.outputType !== 'Csv') {
      dispatch({ type: ActionType.SET_REPORT_INFO, data: requestBody });
    } else {
      try {
        await downloadCsv(requestBody);
      } catch (error) {
        console.log(error);
        notify.warning('Something went wrong, please try again later');
      }
    }
    setIsLoading(false);
  };

  const downloadCsv = async (reportParams: any): Promise<void> => {
    if (reportParams.institutions && reportParams.product && reportParams.institutions.length > 0) {
      for (let i = 0; i < reportParams.product.length; i++) {
        let productChartData: any[] = [];
        let batchPromises: any[] = [];

        for (let j = 0; j < reportParams.institutions.length; j++) {
          let institution = reportParams.institutions[j];
          batchPromises.push(
            fetchChartData({
              request: reportParams?.reportType,
              cuid: institution.value,
              startDate: reportParams?.startDate?.toLocaleDateString('fr-CA'),
              endDate: reportParams?.endDate?.toLocaleDateString('fr-CA'),
              product: reportParams?.product,
              customer: institution.value,
            }).then((value) => ({
              institution: institution.label,
              ...value,
            })),
          );

          // Sends 10 Request at the same time.
          if (batchPromises.length === 10 || j === reportParams.institutions.length - 1) {
            const batchValues = await Promise.all(batchPromises);
            productChartData = productChartData.concat(batchValues);
            batchPromises = [];
          }
        }

        const formattedChartData: TransformChartDataResponse =
          transformIndividualChartData(productChartData);

        const formattedTableData = transformTableData(formattedChartData.totalChartobj);
        const formattedCsvData: (string | number)[][] = [
          ['Individual Site Report'],
          [reportParams.productName],
          [''],
          ['', ...formattedTableData.columns],
        ];

        formattedTableData.rows.map((row) => {
          const rowData: (string | number)[] = [row.name, ...row.values];
          formattedCsvData.push(rowData);
          return {};
        });

        const csvReport = formattedCsvData;
        setDataForDownload(csvReport);
        setItemsAreLoaded(true);
        setTimeout(() => {
          let element: HTMLElement = document.querySelector(
            '.stats-report__download-link',
          ) as HTMLElement;

          if (element) {
            element.click();
          }
        }, 10);
      }
    }
  };

  if (state.reportInfo) return <Redirect to="/report-charts" />;

  return (
    <div className="stats-report">
      {isLoading && (
        <div>
          <PageLoader color={'dark'} size={'large'} type={'border'} />
        </div>
      )}

      <Notifier
        autoClose={5000}
        closeButton={false}
        closeOnClick={false}
        draggable
        draggablePercent={50}
        limit={1}
        newestOnTop
        pauseOnFocusLoss
        pauseOnHover
        position="top-center"
        role="alert"
      />
      <div className="stats-report__text-container">
        <span>
          <strong>
            You have chosen an Individual Site for{' '}
            {individualSiteRequestObj?.requestBody.productName} Starting{' '}
            {individualSiteRequestObj?.requestBody.startDate.toLocaleString('default', {
              month: 'short',
              year: 'numeric',
            })}
            .
          </strong>
          Select the sites you wish to include in your report and click Create Report button to
          continue.
        </span>
      </div>

      <div className="stats-report__list-container">
        <SearchableDropdown
          items={availabledGroup}
          placeholder="Filter by group code"
          onItemSelect={(item) => {
            setSelectedGroup(item);
          }}
        />

        <DualListBox
          onSelection={(items) => setSelectedInstitution(items)}
          options={availableOptions}
          selectedOptionValues={selectedInstitution}
        />

        {individualSiteRequestObj && individualSiteRequestObj.requestBody.outputType !== 'Csv' && (
          <Button
            color="info"
            size="small"
            disabled={selectedInstitution.length === 0}
            className="float-right mt-2"
            onClick={createReport}
            id="create-report"
          >
            Create Report
          </Button>
        )}

        {individualSiteRequestObj && individualSiteRequestObj.requestBody.outputType === 'Csv' && (
          <Button
            color="info"
            size="small"
            disabled={selectedInstitution.length === 0 || isLoading}
            className="float-right mt-2"
            onClick={createReport}
            id="download-report"
          >
            Download Report
          </Button>
        )}

        {individualSiteRequestObj &&
          individualSiteRequestObj.requestBody.outputType === 'Csv' &&
          itemsAreLoaded && (
            <CSVLink
              className="stats-report__download-link stats-report__download-link--hidden float-right mt-2 vol-button vol-button--info vol-button--small"
              data={dataForDownload}
              filename={'detailed-individual-site-report.csv'}
            >
              Hidden Download Link
            </CSVLink>
          )}

        <div className="stats-report__clearfix"></div>
      </div>
    </div>
  );
};

export default StatsReport;
